// Formatering av KOP-broens målinger til den teksten operatøren leser.
//
// HVA SOM ER FLYTTET, OG HVA SOM ER IGJEN:
// de generelle formatererne — peiling, usikkerhet, avstand, frekvens,
// båndbredde, sikkerhet, kurs — ligger nå i `@skade/sensorflate`. Grader, meter
// og hertz betyr det samme på en kystpost og i en grøft, og reglene for antall
// desimaler er nettopp de reglene som ikke skal finnes i to versjoner.
//
// Igjen her står det som bærer KYSTdomenet:
//   * `maalingTekst`/`maalingslagNavn` leser `Maaling`-unionen i `api/typer.ts`,
//     altså KOP-broens wire-format. En delt «måling» måtte vært så vag at den
//     ikke kunne si HVA sensoren målte — og det er hele poenget med den unionen.
//   * `formaterFart` regner i KNOP. En jegertropp måler ikke fart i knop, og en
//     «generell» fartsformaterer med enhetsvalg ville vært en abstraksjon ingen
//     av appene ba om.
//
// HVORFOR EN EGEN FIL MED TESTER (uendret grunn): dette ser ut som pynt, men det
// er selve påstanden. «214°» og «214,3°» sier to helt ulike ting om hvor godt
// RF-en peiler, og «2,4 GHz» og «2437,000 MHz» skiller mellom «noe på
// wifi-båndet» og «denne kanalen». Antall desimaler ER informasjon.
//
// GJENNOMGÅENDE REGEL: enhet ALLTID med, og norsk desimalkomma. En operatør som
// leser «1180» må ikke måtte gjette om det er meter eller yards.
import {
  formaterAvstand, formaterBandbredde, formaterFrekvens, formaterPeiling, formaterUsikkerhet,
} from '@skade/sensorflate';
import type { Maaling } from '../api/typer.js';
import { MAALINGSLAG_NAVN } from '../api/typer.js';

/**
 * HVA SENSOREN FAKTISK MÅLTE, i én linje.
 *
 * Dette er den setningen eierkravet handler om: kontaktkortet skal si hva hver
 * sensor målte, ikke bare at den «ser» kontakten. Derfor formuleres hver variant
 * i sine EGNE enheter — en RF-linje snakker om frekvens, en hydrofon om
 * signatur — og en peiling uten avstand sier «peiling» og ingenting mer.
 */
export function maalingTekst(m: Maaling): string {
  switch (m.slag) {
    case 'peiling':
      return `Peiling ${formaterPeiling(m.peilingDeg, m.usikkerhetDeg)} ${formaterUsikkerhet(m.usikkerhetDeg)}`.trim();
    case 'peiling-avstand':
      return `Peiling ${formaterPeiling(m.peilingDeg, m.usikkerhetDeg)} ${formaterUsikkerhet(m.usikkerhetDeg)} · avstand ${formaterAvstand(m.avstandM)}${m.usikkerhetM !== undefined ? ` ±${Math.round(m.usikkerhetM)} m` : ''}`.replace(/\s+/g, ' ').trim();
    case 'frekvens': {
      const deler = [formaterFrekvens(m.frekvensHz)];
      if (m.bandbreddeHz !== undefined) deler.push(`BB ${formaterBandbredde(m.bandbreddeHz)}`);
      if (m.modulasjon) deler.push(m.modulasjon);
      if (m.signalDbm !== undefined) deler.push(`${Math.round(m.signalDbm)} dBm`);
      if (m.peilingDeg !== undefined) deler.push(`peiling ${formaterPeiling(m.peilingDeg, m.usikkerhetDeg)}`);
      else deler.push('ingen retning');
      return deler.join(' · ');
    }
    case 'lydsignatur': {
      const deler = [m.signaturNavn];
      if (m.snrDb !== undefined) deler.push(`SNR ${Math.round(m.snrDb)} dB`);
      if (m.peilingDeg !== undefined) deler.push(`peiling ${formaterPeiling(m.peilingDeg, m.usikkerhetDeg)}`);
      else deler.push('ingen retning');
      return deler.join(' · ');
    }
    case 'bilde': {
      const deler = [m.bildeUrl ? 'Stillbilde' : 'Bilde ikke levert'];
      if (m.klassifisering) deler.push(m.klassifisering);
      if (m.peilingDeg !== undefined) deler.push(`kamera på ${formaterPeiling(m.peilingDeg)}`);
      if (m.zoom !== undefined) deler.push(`zoom ×${m.zoom}`);
      return deler.join(' · ');
    }
  }
}

/** Kort etikett for måletypen — kolonneoverskrift i kontaktkortet. */
export function maalingslagNavn(m: Maaling): string {
  return MAALINGSLAG_NAVN[m.slag];
}

/** Fart i knop, hele tall. Halve knop er under målestøyen på disse sensorene. */
export function formaterFart(fartKn?: number): string {
  return fartKn === undefined || !Number.isFinite(fartKn) ? '–' : `${Math.round(fartKn)} kn`;
}
